import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Accessibility, X, Plus, Minus, RotateCcw, FileText } from "lucide-react";
import { STATEMENT } from "@/data/site";

const DEFAULTS = { contrast: false, dyslexia: false, highlight: false, reduceMotion: false, fontScale: 0 };
const SCALES = ["100%", "112.5%", "125%"];

function loadSettings() {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("mcc-a11y") || "{}") };
  } catch {
    return DEFAULTS;
  }
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);
  const panelRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("a11y-contrast", settings.contrast);
    root.classList.toggle("a11y-dyslexia", settings.dyslexia);
    root.classList.toggle("a11y-highlight", settings.highlight);
    root.classList.toggle("a11y-reduce-motion", settings.reduceMotion);
    root.style.fontSize = SCALES[settings.fontScale];
    localStorage.setItem("mcc-a11y", JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setStatementOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggle = (key) => setSettings((s) => ({ ...s, [key]: !s[key] }));

  const TOGGLES = [
    { key: "contrast", label: "High Contrast", testId: "a11y-toggle-contrast" },
    { key: "dyslexia", label: "Dyslexia-Friendly Font", testId: "a11y-toggle-dyslexia" },
    { key: "highlight", label: "Highlight Links & Controls", testId: "a11y-toggle-highlight" },
    { key: "reduceMotion", label: "Pause Animations", testId: "a11y-toggle-reduce-motion" },
  ];

  return (
    <>
      <div className="fixed bottom-5 left-5 z-50" data-testid="accessibility-widget">
        <AnimatePresence>
          {open && (
            <motion.div
              ref={panelRef}
              key="a11y-panel"
              role="dialog"
              aria-label="Accessibility options"
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              className="mb-4 w-72 rounded-md border border-[rgba(212,175,55,0.4)] bg-[#111115] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
              data-testid="accessibility-panel"
            >
              <p className="eyebrow mb-4">Accessibility Options</p>

              <div className="mb-4">
                <p className="text-xs text-[#A1A1AA] mb-2">Text Size</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, fontScale: Math.max(0, s.fontScale - 1) }))}
                    aria-label="Decrease text size"
                    data-testid="a11y-text-decrease"
                    className="flex-1 border border-[rgba(212,175,55,0.35)] py-2 text-[#F3E081] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center gap-1 text-sm"
                  >
                    <Minus className="h-3.5 w-3.5" aria-hidden="true" /> A
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettings((s) => ({ ...s, fontScale: Math.min(2, s.fontScale + 1) }))}
                    aria-label="Increase text size"
                    data-testid="a11y-text-increase"
                    className="flex-1 border border-[rgba(212,175,55,0.35)] py-2 text-[#F3E081] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center gap-1 text-sm"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" /> A
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {TOGGLES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => toggle(t.key)}
                    aria-pressed={settings[t.key]}
                    data-testid={t.testId}
                    className={`w-full flex items-center justify-between border px-3 py-2.5 text-sm transition-colors ${
                      settings[t.key]
                        ? "border-[#D4AF37] bg-[rgba(212,175,55,0.12)] text-[#F3E081]"
                        : "border-[#27272A] text-[#A1A1AA] hover:border-[rgba(212,175,55,0.5)]"
                    }`}
                  >
                    {t.label}
                    <span
                      aria-hidden="true"
                      className={`h-4 w-7 rounded-full relative transition-colors ${settings[t.key] ? "bg-[#D4AF37]" : "bg-[#27272A]"}`}
                    >
                      <span
                        className={`absolute top-0.5 h-3 w-3 rounded-full bg-black transition-transform ${settings[t.key] ? "translate-x-3.5" : "translate-x-0.5"}`}
                      />
                    </span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setStatementOpen(true)}
                data-testid="a11y-statement-button"
                className="mt-4 w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black py-2.5 text-sm font-semibold hover:bg-[#F3E081] transition-colors"
              >
                <FileText className="h-4 w-4" aria-hidden="true" /> Accessibility Statement
              </button>
              <button
                type="button"
                onClick={() => setSettings(DEFAULTS)}
                data-testid="a11y-reset-button"
                className="mt-2 w-full flex items-center justify-center gap-2 py-2 text-xs text-[#71717A] hover:text-[#F3E081] transition-colors"
              >
                <RotateCcw className="h-3 w-3" aria-hidden="true" /> Reset all settings
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close accessibility options" : "Open accessibility options"}
          aria-expanded={open}
          data-testid="accessibility-widget-button"
          className="h-14 w-14 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-[0_10px_30px_rgba(212,175,55,0.35)] hover:bg-[#F3E081] hover:scale-105 transition-all duration-300"
        >
          {open ? <X className="h-7 w-7" aria-hidden="true" /> : <Accessibility className="h-7 w-7" aria-hidden="true" />}
        </button>
      </div>

      <AnimatePresence>
        {statementOpen && (
          <motion.div
            key="a11y-statement"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setStatementOpen(false)}
            data-testid="accessibility-statement-modal"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={STATEMENT.title}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-md border border-[rgba(212,175,55,0.4)] bg-[#111115] p-6 sm:p-10"
            >
              <button
                type="button"
                onClick={() => setStatementOpen(false)}
                aria-label="Close accessibility statement"
                data-testid="a11y-statement-close"
                className="absolute top-4 right-4 p-2 text-[#A1A1AA] hover:text-[#F3E081]"
              >
                <X className="h-5 w-5" />
              </button>
              <h2 className="font-display text-3xl text-[#F4F2EE]">{STATEMENT.title}</h2>
              <p className="mt-1 font-mono-accent text-xs tracking-[0.15em] text-[#D4AF37]">{STATEMENT.updated}</p>
              <div className="hairline-gold my-6" />
              {STATEMENT.sections.map((s) => (
                <section key={s.heading} className="mb-7">
                  <h3 className="font-display text-xl text-[#F3E081] mb-2">{s.heading}</h3>
                  <p className="text-sm leading-relaxed text-[#A1A1AA]">{s.body}</p>
                  {s.list && (
                    <ul className="mt-3 space-y-1.5 text-sm text-[#A1A1AA] list-disc list-inside">
                      {s.list.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {s.after && <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">{s.after}</p>}
                </section>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
